import {
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import moment from "moment";
import { Observable, Subject, takeUntil, timer } from "rxjs";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Area } from "src/app/shared/model/tickets/area.model";
import { Prioridad } from "src/app/shared/model/tickets/prioridad.model";
import { Tickets } from "src/app/shared/model/tickets/tickets.model";
import { SweetAlertComponent } from "src/app/shared/SweetMesssage";
import { Constantes } from "src/app/utilerias/constantes/constantes";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";
import { MensajesFijosEnum } from "src/app/utilerias/enums/mensajes-fijos-enum";
import { TicketPrioridad } from "src/app/utilerias/enums/ticket-prioridad.enum";
import { TicketsEstatus } from "src/app/utilerias/enums/tickets-estatus.enum";
import { TicketsService } from "../tickets.service";

export interface TicketPaginationState {
  position: number;
  totalItems: number;
  totalPages: number;
}

/**
 * Base de las columnas del tablero de tickets: la del cliente (NotificacionCteTicketComponent)
 * y la de atención (NotificacionTicketComponent). Aquí está lo que comparten: la consulta por
 * estatus y la paginada, el semáforo de tiempo y los textos de cada tarjeta. Cada columna dice
 * con qué servicio consulta, cómo arranca la carga y cómo abre el detalle de un ticket.
 */
@Directive()
export abstract class ColumnaTicketsBaseComponent implements OnInit, OnDestroy {
  protected readonly destroy$ = new Subject<void>();
  private readonly shortMonthNames = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];

  tickets = new Tickets();
  listaTickets!: ConsultaList<Tickets>;
  list: Tickets[] = [];
  @Input() estatusSolicitud!: TicketsEstatus;
  @Input() nombreCorto = "";
  @Input() numero: number | null = null;
  @Input() fechaSeleccionada: Date | string | null = null;
  @Input() areaSeleccionada: Area | null = null;
  @Input() columnTitle = "";
  @Input() columnColor = "";
  date = new FormControl(new Date());
  regInicial = 0;
  regXPagina = 5;
  position = 0;
  @Output() numPaginasEvent = new EventEmitter<any>();
  @Output() paginationStateChange = new EventEmitter<TicketPaginationState>();

  prioridades: Prioridad[] = [];
  dateNow: Date = new Date();
  tiempoAlta = 0;
  tiempoMedia = 0;
  tiempoBaja = 0;
  horaSolicitud: any;
  fechaOriginal: any;
  fechaHoy: any;
  prioridadAMB: any;
  @Output() TimerExpired: EventEmitter<any> = new EventEmitter<any>();
  @Input() SearchDate: moment.Moment = moment();
  @Input() ElapsTime = 1;
  searchEndDate: moment.Moment;
  remainingTime = 0;
  minutes = 0;
  seconds = 0;
  everySecond: Observable<number> = timer(0, 30000);

  constructor(
    protected readonly mensaje: SweetAlertComponent,
    protected readonly service: TicketsService,
    protected readonly router: Router
  ) {
    this.searchEndDate = this.SearchDate.add(this.ElapsTime, "minutes");
  }

  /** Carga la columna con los valores que le fijó el tablero. */
  abstract llamado(): void;

  /** Consulta sin paginar de los tickets del estatus de la columna. */
  protected abstract consultarPorEstatus(ticket: Tickets): Observable<any>;

  /** Consulta paginada con los filtros del tablero. */
  protected abstract consultarFiltrados(consulta: ConsultaList<Tickets>): Observable<any>;

  /** Abre el detalle del ticket que se pulsó en la columna. */
  protected abstract mostrarDetalleTicket(datos: Tickets): void;

  ngOnInit(): void {
    this.cargarNivelesDeServicio();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByTicketId(_index: number, ticket: Tickets): number {
    return ticket.id_ticket;
  }

  getPriorityClass(priority: string | TicketPrioridad | undefined): string {
    switch (String(priority ?? "").toUpperCase()) {
      case TicketPrioridad.ALTA:
        return "ticket-admin-card__badge--priority-high";
      case TicketPrioridad.MEDIA:
        return "ticket-admin-card__badge--priority-medium";
      case TicketPrioridad.BAJA:
        return "ticket-admin-card__badge--priority-low";
      default:
        return "ticket-admin-card__badge--priority-default";
    }
  }

  getCardCategoryLabel(ticket: Tickets): string {
    return String(
      ticket.areaResolutora ??
        ticket.tipoSolicitud ??
        ticket.areaSolicitante ??
        ""
    ).trim();
  }

  getCardFooterAreaLabel(ticket: Tickets): string {
    return String(ticket.areaSolicitante ?? ticket.areaResolutora ?? "").trim();
  }

  getStatusChipLabel(ticket: Tickets): string {
    if (ticket.actualizado) {
      return "Nuevo";
    }

    return String(ticket.nombreEstaus ?? this.columnTitle ?? "").trim();
  }

  getCardDateLabel(ticket: Tickets): string {
    const parsed = this.resolveCardDate(ticket);
    if (parsed) {
      return this.buildFormattedDate(parsed);
    }

    const source = ticket.fechaSolicitudConFormato ?? ticket.fechaSolicitud;
    return source ? String(source).trim() : "";
  }

  getCardDateDayLabel(ticket: Tickets): string {
    const parsed = this.resolveCardDate(ticket);
    if (!parsed) {
      return this.getCardDateLabel(ticket);
    }

    const month = this.shortMonthNames[parsed.month()] ?? "";
    return `${parsed.date()} ${month} ${parsed.year()}`;
  }

  getCardDateTimeLabel(ticket: Tickets): string {
    const parsed = this.resolveCardDate(ticket);
    if (!parsed) {
      return "";
    }

    const hours = String(parsed.hour()).padStart(2, "0");
    const minutes = String(parsed.minute()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  private resolveCardDate(ticket: Tickets): moment.Moment | null {
    const source = ticket.fechaSolicitudConFormato ?? ticket.fechaSolicitud;
    if (!source) {
      return null;
    }

    const value = String(source).trim();
    if (!value) {
      return null;
    }

    const parsed = moment.parseZone(value, moment.ISO_8601, true);
    if (parsed.isValid()) {
      return parsed;
    }

    const fallback = moment(value);
    if (fallback.isValid()) {
      return fallback;
    }

    return null;
  }

  cargarNivelesDeServicio(): void {
    this.service
      .consultarPrioridad()
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {
        if (resp?.list?.length) {
          this.prioridades = resp.list;
          for (const prioridad of this.prioridades) {
            if (prioridad.nombre === TicketPrioridad.ALTA) {
              this.tiempoAlta = prioridad.tiempo_en_horas;
            } else if (prioridad.nombre === TicketPrioridad.MEDIA) {
              this.tiempoMedia = prioridad.tiempo_en_horas;
            } else {
              this.tiempoBaja = prioridad.tiempo_en_horas;
            }
          }
        }
      });
  }

  cargarSolicitudesPorEstatus(): void {
    this.tickets = new Tickets();
    this.tickets.id_estatus = this.estatusSolicitud;
    this.completarConsulta(this.tickets);

    if (this.areaSeleccionada?.id !== null && this.areaSeleccionada?.id !== undefined) {
      this.tickets.id_area = this.areaSeleccionada.id;
      this.cargarSolicitudesPorEstatus2();
      return;
    }

    this.service
      .consultarArea(UrlConstantes.consultarAreaUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res2) => {
        const areasUsuario = Array.isArray(res2?.list) ? res2.list : [];
        if (!areasUsuario.length) {
          this.list = [];
          this.emitPagination(0, 0);
          return;
        }

        this.tickets.id_area = areasUsuario[Constantes.CERO].id;
        this.cargarSolicitudesPorEstatus2();
      });
  }

  private cargarSolicitudesPorEstatus2(): void {
    this.consultarPorEstatus(this.tickets)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response && this.validarCodigoRetorno(response.pCodRetorno)) {
          const tickets = Array.isArray(response.listaDeTiCkets) ? response.listaDeTiCkets : [];
          this.list = tickets.map((ticket) => this.prepareTicket(ticket));
          this.emitPagination(this.list.length, 1);
          return;
        }

        this.list = [];
        this.emitPagination(0, 0);
        this.mensaje.showSwal(
          "info-rule",
          response?.pMensaje ?? MensajesFijosEnum.ERROR_SERVIDOR
        );
      });
  }

  semaforoTiempo(ticket: Tickets): void {
    this.fechaHoy = new Date();
    this.horaSolicitud = ticket.fechaSolicitud;
    this.fechaOriginal = new Date(this.horaSolicitud);

    if (ticket.prioridad === TicketPrioridad.ALTA) {
      this.validacionSemaforo(this.tiempoAlta, ticket);
    } else if (ticket.prioridad === TicketPrioridad.MEDIA) {
      this.validacionSemaforo(this.tiempoMedia, ticket);
    } else {
      this.validacionSemaforo(this.tiempoBaja, ticket);
    }
  }

  validacionSemaforo(tiempoLimite: number, ticket: Tickets): void {
    const tiempoPrioridadMilisegundos = 1000 * 60 * 60 * tiempoLimite;
    const tiempoTot = this.fechaOriginal.getTime() + tiempoPrioridadMilisegundos;
    const tiempoT = new Date(tiempoTot);
    const mitadPrioridad = tiempoLimite / 2;
    const tiempoMitadMilisegundos = 1000 * 60 * 60 * mitadPrioridad;
    const tiempoTotM = this.fechaOriginal.getTime() + tiempoMitadMilisegundos;
    const tiempoTM = new Date(tiempoTotM);

    if (this.fechaHoy < tiempoTM) {
      ticket.color = Constantes.GREEN;
      ticket.tiemp = Constantes.ENTIEMPO;
    } else if (this.fechaHoy > tiempoTM && this.fechaHoy < tiempoT) {
      ticket.color = Constantes.YELLOW;
      ticket.tiemp = Constantes.ADESTIEMPO;
    } else if (this.fechaHoy > tiempoT) {
      ticket.color = Constantes.RED;
      ticket.tiemp = Constantes.FUERADETIEMPO;
    } else {
      ticket.color = Constantes.PINK;
    }
  }

  validarCodigoRetorno(codigo: string): boolean {
    return this.convertirANumbero(codigo) === 0;
  }

  convertirANumbero(numero: any): number {
    if (numero !== null && numero !== undefined) {
      try {
        return Number(numero);
      } catch {
        return -1;
      }
    }

    return -1;
  }

  cargarSolicitudesFiltradas(ticket: Tickets): void {
    this.listaTickets = new ConsultaList<Tickets>();
    this.completarConsulta(ticket);
    this.listaTickets.regInicial = this.regInicial;
    this.listaTickets.regXPagina = this.regXPagina;
    this.listaTickets.bean = ticket;

    this.consultarFiltrados(this.listaTickets)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response && this.validarCodigoRetorno(response.pCodRetorno)) {
          const tickets = Array.isArray(response.list) ? response.list : [];
          this.list = tickets.map((item) => this.prepareTicket(item));
          const totalItems = this.resolveTotalItems(response, this.list);
          const totalPages = this.resolveTotalPages(response, totalItems);
          this.numPaginasEvent.emit([totalPages, this.position, totalItems]);
          this.emitPagination(totalItems, totalPages);
          return;
        }

        this.list = [];
        this.numPaginasEvent.emit([0, this.position, 0]);
        this.emitPagination(0, 0);
        this.mensaje.showSwal(
          "info-rule",
          response?.pMensaje ?? MensajesFijosEnum.ERROR_SERVIDOR
        );
      });
  }

  inicializarValoresPaginacion(
    nom: string,
    date: Date | string | null,
    area: Area | null,
    regInicial: number,
    regXPagina: number,
    position: number,
    numero: number | null
  ): void {
    this.nombreCorto = nom;
    this.fechaSeleccionada = date;
    this.areaSeleccionada = area;
    this.regInicial = regInicial;
    this.regXPagina = regXPagina;
    this.numero = numero;
    this.position = position;
  }

  cargarDatosTicket(ticket: Tickets): void {
    const datosTicket = Object.assign(new Tickets(), ticket);
    if (!datosTicket.fechaSolicitudConFormato && datosTicket.fechaSolicitud) {
      datosTicket.fechaSolicitudConFormato = String(datosTicket.fechaSolicitud);
    }

    if (datosTicket.actualizado) {
      this.service
        .cambiarBanderaTicket({ id_ticket: datosTicket.id_ticket })
        .pipe(takeUntil(this.destroy$))
        .subscribe();
      datosTicket.actualizado = false;
    }

    this.mostrarDetalleTicket(datosTicket);
  }

  /**
   * Datos propios de la vista que viajan en el ticket de cada consulta. La de atención no
   * agrega nada; la del cliente la sobrescribe para acotar la consulta a su usuario.
   */
  protected completarConsulta(_ticket: Tickets): void {
    // La vista de atención consulta los tickets sin acotarlos a un usuario.
  }

  protected emitPagination(totalItems: number, totalPages: number): void {
    this.paginationStateChange.emit({
      position: this.position,
      totalItems,
      totalPages,
    });
  }

  private prepareTicket(ticket: Tickets): Tickets {
    const preparedTicket = ticket;
    preparedTicket.siguiente = [
      TicketsEstatus.ABIERTA,
      TicketsEstatus.PROCESO,
      TicketsEstatus.ATENDIDA,
      TicketsEstatus.REABIERTA,
    ].includes(ticket.id_estatus);
    preparedTicket.aTiempo = true;

    if (!preparedTicket.fechaSolicitudConFormato && preparedTicket.fechaSolicitud) {
      preparedTicket.fechaSolicitudConFormato = String(preparedTicket.fechaSolicitud);
    }

    this.semaforoTiempo(preparedTicket);
    return preparedTicket;
  }

  private resolveTotalItems(response: ConsultaList<Tickets>, tickets: Tickets[]): number {
    const payload = response as ConsultaList<Tickets> & Record<string, unknown>;
    const candidates = [
      payload?.param4,
      payload?.pTotalItems,
      payload?.totalItems,
      payload?.totalRegistros,
      payload?.totalElements,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }

    const currentItems = Array.isArray(tickets) ? tickets.length : 0;
    if (currentItems === 0) {
      return 0;
    }

    return this.regInicial + currentItems;
  }

  private resolveTotalPages(response: ConsultaList<Tickets>, totalItems: number): number {
    const payload = response as ConsultaList<Tickets> & Record<string, unknown>;
    const candidates = [payload?.totalPaginas, payload?.paginaTotal];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    if (totalItems <= 0 || this.regXPagina <= 0) {
      return 0;
    }

    return Math.ceil(totalItems / this.regXPagina);
  }

  private buildFormattedDate(dateValue: moment.Moment): string {
    const month = this.shortMonthNames[dateValue.month()] ?? "";
    const hours = String(dateValue.hour()).padStart(2, "0");
    const minutes = String(dateValue.minute()).padStart(2, "0");
    return `${dateValue.date()} ${month} ${dateValue.year()} ${hours}:${minutes}`;
  }
}
